<template>
  <div class="attribute-panel">
    <div class="panel-header">
      <a-button type="primary" @click="handleAdd" :loading="loading">
        <template #icon>
          <PlusOutlined />
        </template>
        添加{{ type === 'color' ? '颜色' : '尺寸' }}
      </a-button>
      
      <a-button 
        type="primary" 
        @click="handleSave" 
        :loading="loading"
        style="margin-left: 8px"
        :disabled="!hasChanges"
      >
        <template #icon>
          <SaveOutlined />
        </template>
        保存更改
      </a-button>
    </div>
    
    <a-card>
      <div class="attributes-container">
        <draggable
          v-model="localAttributes"
          item-key="_id"
          handle=".drag-handle"
          @end="onDragEnd"
          :disabled="loading"
        >
          <template #item="{ element }">
            <div class="attribute-item">
              <div class="attribute-form">
                <div class="drag-handle">
                  <HolderOutlined />
                </div>
                
                <a-input
                  v-model:value="element.value"
                  :placeholder="`请输入${type === 'color' ? '颜色' : '尺寸'}名称`"
                  style="flex: 1; margin: 0 12px;"
                  @change="markAsChanged"
                />
                
                <a-button
                  type="text"
                  danger
                  size="small"
                  @click="handleDelete(element)"
                  :loading="loading"
                >
                  <template #icon>
                    <DeleteOutlined />
                  </template>
                </a-button>
              </div>
            </div>
          </template>
        </draggable>
        
        <div v-if="localAttributes.length === 0" class="empty-state">
          <a-empty :description="`暂无${type === 'color' ? '颜色' : '尺寸'}数据`">
            <a-button type="primary" @click="handleAdd">
              添加第一个{{ type === 'color' ? '颜色' : '尺寸' }}
            </a-button>
          </a-empty>
        </div>
      </div>
    </a-card>
  </div>
</template>

<script>
import { ref, watch, toRefs } from 'vue'
import { PlusOutlined, SaveOutlined, HolderOutlined, DeleteOutlined } from '@ant-design/icons-vue'
import { message } from 'ant-design-vue'
import draggable from 'vuedraggable'

export default {
  name: 'AttributePanel',
  components: {
    PlusOutlined,
    SaveOutlined,
    HolderOutlined,
    DeleteOutlined,
    draggable
  },
  props: {
    type: {
      type: String,
      required: true,
      validator: (value) => ['color', 'size'].includes(value)
    },
    attributes: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  emits: ['save', 'add', 'delete'],
  setup(props, { emit }) {
    const { attributes } = toRefs(props)
    const localAttributes = ref([])
    const hasChanges = ref(false)
    
    // 监听属性变化，同步到本地数据
    watch(attributes, (newAttributes, oldAttributes) => {
      // 为每个属性添加唯一的ID以确保Vue能正确跟踪组件
      localAttributes.value = newAttributes.map((attr, index) => ({
        ...attr,
        _id: attr.id || `temp_${index}_${Date.now()}`
      }))
      // 如果数组长度增加了，说明添加了新项目，需要标记为已更改
      if (oldAttributes && newAttributes.length > oldAttributes.length) {
        hasChanges.value = true
      } else {
        hasChanges.value = false
      }
    }, { immediate: true, deep: true })
    
    const markAsChanged = () => {
      hasChanges.value = true
    }
    
    const handleDelete = (element) => {
      const index = localAttributes.value.findIndex(attr => attr === element)
      if (index > -1) {
        localAttributes.value.splice(index, 1)
        // 重新设置orderNum
        localAttributes.value.forEach((attr, idx) => {
          attr.orderNum = idx + 1
        })
        hasChanges.value = true
      }
    }
    
    const onDragEnd = () => {
      // 拖拽结束后重新设置orderNum
      localAttributes.value.forEach((attr, index) => {
        attr.orderNum = index + 1
      })
      hasChanges.value = true
    }
    
    const handleAdd = () => {
      emit('add', props.type)
    }
    
    const handleSave = () => {
      // 验证数据
      const invalidItems = localAttributes.value.filter(attr => !attr.value || !attr.value.trim())
      if (invalidItems.length > 0) {
        message.warning(`请填写所有${props.type === 'color' ? '颜色' : '尺寸'}名称`)
        return
      }
      
      // 检查重复值
      const values = localAttributes.value.map(attr => attr.value.trim().toLowerCase())
      const duplicates = values.filter((value, index) => values.indexOf(value) !== index)
      if (duplicates.length > 0) {
        message.warning(`存在重复的${props.type === 'color' ? '颜色' : '尺寸'}名称`)
        return
      }
      
      emit('save', props.type, localAttributes.value)
      hasChanges.value = false
    }
    
    return {
      localAttributes,
      hasChanges,
      markAsChanged,
      handleDelete,
      onDragEnd,
      handleAdd,
      handleSave
    }
  }
}
</script>

<style scoped>
.attribute-panel {
  padding: 0;
}

.panel-header {
  margin-bottom: 16px;
  display: flex;
  align-items: center;
}

.attributes-container {
  min-height: 200px;
}

.attribute-item {
  margin-bottom: 12px;
  padding: 12px;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  background: #fafafa;
  cursor: move;
}

.attribute-item:hover {
  border-color: #1890ff;
  background: #f6fffd;
}

.attribute-form {
  display: flex;
  align-items: center;
}

.drag-handle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: #999;
  cursor: grab;
  margin-right: 12px;
}

.drag-handle:hover {
  color: #1890ff;
}

.drag-handle:active {
  cursor: grabbing;
}


.empty-state {
  text-align: center;
  padding: 40px 0;
}

/* 拖拽时的样式 */
.sortable-ghost {
  opacity: 0.5;
}

.sortable-chosen {
  background: #e6f4ff;
  border-color: #1890ff;
}
</style>